<template>
  <div class="rich-editor" :style="{ borderColor: focused ? '#679ff6' : '#dcdfe6' }">
    <Toolbar
      class="rich-toolbar"
      :editor="editorRef"
      :default-config="toolbarConfig"
      mode="default"
    />
    <Editor
      class="rich-content"
      :style="{ height: height + 'px' }"
      v-model="valueHtml"
      :default-config="editorConfig"
      mode="default"
      @on-created="handleCreated"
      @on-change="handleChange"
      @on-focus="focused = true"
      @on-blur="focused = false"
    />
    <div class="rich-tip">
      <span>支持图文混排，图片自动上传到服务器</span>
      <span>建议正文图片宽度 ≥ 750px，单图 ≤ 2MB</span>
    </div>
  </div>
</template>

<script>
import '@wangeditor/editor/dist/css/style.css'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { shallowRef, onBeforeUnmount } from 'vue'
import globle from '../../globle/globle'

// 入口：把数据库里相对路径(material/...)的 <img src> 补成完整 URL，让 wangEditor 能显示
const RE_EXPAND_SRC = /(<img\b[^>]*?\ssrc=")(?!https?:\/\/|data:)([^"]+)(")/gi
// 出口：把 <img src> 里 host + /SMY_TONGUE/ 前缀剥掉，只存相对路径，避免环境切换失效
const RE_STRIP_SRC = /(<img\b[^>]*?\ssrc=")https?:\/\/[^/]+\/SMY_TONGUE\/([^"]+)(")/gi

function expandHtml(html) {
  if (!html) return ''
  const base = globle && globle.imgPath
  if (!base) return html
  return String(html).replace(RE_EXPAND_SRC, (_, pre, src, post) =>
    pre + base + src.replace(/^\//, '') + post)
}

function stripHtml(html) {
  if (!html) return ''
  return String(html).replace(RE_STRIP_SRC, '$1$2$3')
}

export default {
  name: 'RichEditor',
  components: { Editor, Toolbar },
  props: {
    modelValue: { type: String, default: '' },
    height: { type: Number, default: 380 },
    placeholder: { type: String, default: '请输入商品详情，可插入图片、设置标题、对齐方式…' },
    maxImageSizeMB: { type: Number, default: 2 },
  },
  emits: ['update:modelValue', 'change'],
  setup() {
    const editorRef = shallowRef()
    onBeforeUnmount(() => {
      const editor = editorRef.value
      if (editor) editor.destroy()
    })
    return { editorRef }
  },
  data() {
    return {
      GLOBAL: globle,
      focused: false,
      valueHtml: expandHtml(this.modelValue || ''),
      toolbarConfig: {
        toolbarKeys: [
          'headerSelect',
          '|',
          'bold',
          'italic',
          'underline',
          'through',
          'color',
          'bgColor',
          '|',
          'fontSize',
          'lineHeight',
          '|',
          'bulletedList',
          'numberedList',
          'justifyLeft',
          'justifyCenter',
          'justifyRight',
          '|',
          'insertLink',
          'uploadImage',
          'divider',
          '|',
          'undo',
          'redo',
          'clearStyle',
        ],
      },
      editorConfig: {
        placeholder: this.placeholder,
        MENU_CONF: {
          uploadImage: {
            customUpload: this.handleUploadImage,
          },
        },
      },
    }
  },
  watch: {
    modelValue(v) {
      const expanded = expandHtml(v || '')
      if (expanded !== this.valueHtml) this.valueHtml = expanded
    },
  },
  methods: {
    handleCreated(editor) {
      this.editorRef = editor
    },
    handleChange(editor) {
      const html = editor.getHtml()
      this.valueHtml = html
      const clean = stripHtml(html)
      this.$emit('update:modelValue', clean)
      this.$emit('change', clean)
    },
    handleUploadImage(file, insertFn) {
      const okType = /image\/(jpeg|png|webp|gif)/.test(file.type)
      if (!okType) {
        this.$message.error('仅支持 jpg / png / webp / gif')
        return
      }
      if (file.size > this.maxImageSizeMB * 1024 * 1024) {
        this.$message.error(`图片大小不能超过 ${this.maxImageSizeMB}MB`)
        return
      }
      const fd = new FormData()
      fd.append('file', file)
      this.$multipartPost('/business/base/image/upload', fd)
        .then((res) => {
          if (res.data && res.data.code === 0) {
            const path = res.data.msg || res.data.data || ''
            if (!path) {
              this.$message.error('上传成功但未返回图片路径')
              return
            }
            const fullUrl = /^https?:\/\//i.test(path)
              ? path
              : (this.GLOBAL && this.GLOBAL.imgPath ? this.GLOBAL.imgPath + path : path)
            insertFn(fullUrl, '', fullUrl)
          } else {
            this.$message.error((res.data && res.data.msg) || '图片上传失败')
          }
        })
        .catch((err) => {
          console.error('富文本图片上传失败', err)
          this.$message.error('图片上传失败')
        })
    },
  },
}
</script>

<style scoped>
.rich-editor {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  transition: border-color 0.2s;
  overflow: hidden;
}
.rich-toolbar {
  border-bottom: 1px solid #ebeef5;
  background: #fafbfc;
}
.rich-content {
  overflow-y: auto;
}
.rich-tip {
  display: flex;
  justify-content: space-between;
  padding: 6px 12px;
  font-size: 12px;
  color: #909399;
  background: #fafbfc;
  border-top: 1px solid #ebeef5;
}
</style>
